import type { CpvFlat } from "../../api/codCpvTreeApi";

export interface CpvNode {
  code: string;
  label: string;
  clasificacion: string | null;
  level: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  children: CpvNode[];
  parent: CpvNode | null;
}

export interface BuiltTree {
  roots: CpvNode[];
  byCode: Map<string, CpvNode>;
}

export const levelOf = (code: string): CpvNode["level"] => {
  // Profundidad = 8 - trailing zeros. Mínimo 1 (división), máximo 8 (subcategoría).
  const match = code.match(/0+$/);
  const zeros = match ? match[0].length : 0;
  const lvl = Math.max(1, Math.min(8, 8 - zeros));
  return lvl as CpvNode["level"];
};

// Genera el código del ancestro al nivel pedido. Ej: maskToLevel("03111000", 2) = "03100000"
const maskToLevel = (code: string, lvl: number): string => {
  const significant = code.slice(0, lvl);
  return significant.padEnd(8, "0");
};

const findExistingAncestor = (
  code: string,
  byCode: Map<string, CpvNode>
): CpvNode | null => {
  const myLevel = levelOf(code);
  for (let lvl = myLevel - 1; lvl >= 1; lvl--) {
    const mask = maskToLevel(code, lvl);
    const node = byCode.get(mask);
    if (node) return node;
  }
  return null;
};

const stripPrefix = (code: string, label: string): string => {
  // Las descripciones vienen como "03000000-1 Productos de la agricultura...".
  // Limpiamos para que no aparezca duplicado el código.
  const cleaned = label.replace(new RegExp(`^${code}-\\d+\\s*`), "").trim();
  return cleaned || label;
};

export const buildTree = (flat: CpvFlat[]): BuiltTree => {
  const sorted = [...flat].sort((a, b) =>
    a.cod_cpv < b.cod_cpv ? -1 : a.cod_cpv > b.cod_cpv ? 1 : 0
  );

  const byCode = new Map<string, CpvNode>();
  for (const item of sorted) {
    byCode.set(item.cod_cpv, {
      code: item.cod_cpv,
      label: stripPrefix(item.cod_cpv, item.cpv),
      clasificacion: item.cod_clasificacion || null,
      level: levelOf(item.cod_cpv),
      children: [],
      parent: null,
    });
  }

  const roots: CpvNode[] = [];
  for (const node of byCode.values()) {
    const parent = findExistingAncestor(node.code, byCode);
    if (parent) {
      node.parent = parent;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return { roots, byCode };
};

export const collectDescendants = (node: CpvNode): string[] => {
  const out: string[] = [];
  const walk = (n: CpvNode) => {
    for (const child of n.children) {
      out.push(child.code);
      walk(child);
    }
  };
  walk(node);
  return out;
};

// Devuelve la rama completa de un nodo: él mismo + todos sus descendientes.
export const branchCodes = (node: CpvNode): string[] => [
  node.code,
  ...collectDescendants(node),
];

export type NodeState = "checked" | "partial";

export interface SelectionDerived {
  // code -> estado visual. Los códigos ausentes están unchecked.
  state: Map<string, NodeState>;
}

// Cada CPV en `selected` es explícito (se guarda tal cual en el CSV). El estado
// visual de cada nodo se deriva de si su rama está completa/parcialmente en selected.
// Recorrido post-orden O(N): un nodo es 'checked' si está en selected y todos sus
// descendientes también; 'partial' si él o algún descendiente está en selected pero
// la rama no está completa; ausente = unchecked.
export const deriveSelection = (
  tree: BuiltTree,
  selected: Set<string>
): SelectionDerived => {
  const state = new Map<string, NodeState>();
  if (selected.size === 0) return { state };

  // Devuelve [totalEnRama, seleccionadosEnRama] para el nodo (incluyéndolo).
  const walk = (node: CpvNode): [number, number] => {
    let total = 1;
    let sel = selected.has(node.code) ? 1 : 0;
    for (const child of node.children) {
      const [t, s] = walk(child);
      total += t;
      sel += s;
    }
    if (sel > 0) {
      state.set(node.code, sel === total ? "checked" : "partial");
    }
    return [total, sel];
  };

  for (const root of tree.roots) walk(root);
  return { state };
};

const normalize = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    // Eliminar combining diacritical marks (U+0300 a U+036F)
    .replace(/[̀-ͯ]/g, "");

export interface SearchResult {
  matchSet: Set<string>;
  ancestorsToExpand: Set<string>;
}

export const searchTree = (
  tree: BuiltTree,
  query: string
): SearchResult | null => {
  const q = normalize(query.trim());
  if (!q) return null;

  const matchSet = new Set<string>();
  const ancestorsToExpand = new Set<string>();

  for (const node of tree.byCode.values()) {
    if (
      node.code.includes(q) ||
      normalize(node.label).includes(q) ||
      (node.clasificacion && normalize(node.clasificacion).includes(q))
    ) {
      matchSet.add(node.code);
      let p = node.parent;
      while (p) {
        ancestorsToExpand.add(p.code);
        p = p.parent;
      }
    }
  }

  return { matchSet, ancestorsToExpand };
};
